import { AppType, StellaxAiReportType } from '~/graphql/types';
import type { AppConfig } from '../types';
import type { OptionOf } from '~/components/molecule/Dropdown';
import type { PriceWithInterval } from '~/util/getPriceWithInterval';
import getPriceWithInterval from '~/util/getPriceWithInterval';

const description = `## Omschrijving
Ben je op zoek naar nieuwe klanten voor de verhuur en beheer van vastgoed? Transformeer dan je website tot een krachtige leadgeneratie machine door je websitebezoekers te voorzien van gepersonaliseerde verhuur-rapporten.

De rapporten worden opgesteld door Stellax.ai, een software- en dataleverancier voor vastgoedprofessionals. Je hebt keuze uit twee type rapporten, één met de huurprijs en WWS punten. Het tweede rapport biedt daarnaast inzicht in de leegwaarde en presenteert een bruto/netto rendement en cash-flow berekening. Het rapport wordt in jouw huisstijl opgesteld en kan ongelimiteerd (fair-use) worden opgevraagd.

Na het activeren van de app kies zelf welk type rapport je wilt aanbieden. Dit kan later nog worden aangepast.

**Type rapport** (Tarief p/m (excl. btw))

- Huurprijs en WWS-punten check - € 50,- ([voorbeeld](https://dhstash.s3.eu-west-1.amazonaws.com/app/stellax/Kastelenstraat_213-3_1082EE_Amsterdam_standaard.pdf))
- Huurprijs, WWS-punten check, leegwaarde en rendement- en cashflow analyse - € 80,- ([voorbeeld](https://dhstash.s3.eu-west-1.amazonaws.com/app/stellax/Kastelenstraat_213-3_1082EE_Amsterdam_uitgebreid.pdf))
`;

const termsOfUse = `Deze app wordt aangeboden door DatHuis BV in samenwerking met Stellax BV.

Door het activeren van deze app ga je akkoord met de voorwaarden van Stellax en geef je DatHuis toestemming om gegevens uit te wisselen tussen jouw DatHuis account en het platform van Stellax. Daarbij is DatHuis de verwerker van jouw gegevens. Hoe we gegevens verwerken staat beschreven in de verwerkersovereenkomst. Een kopie van de verwerkersovereenkomst en algemene voorwaarden zijn beschikbaar op de abonnementspagina in de instellingen van jouw account. Op de dienstverlening van Stellax B.V. zijn hun voorwaarden van toepassing. Deze vind je op
[https://stellax.ai/terms_of_use](https://stellax.ai/terms_of_use).`;

export const APP_HELP_LINK =
  'https://help.dathuis.nl/nl/articles/9145218-over-de-stellax-ai-property-scan-app';

const PRICES: Array<PriceWithInterval> = [
  {
    amount: 5000,
    currency: 'EUR',
    interval: {
      amount: 1,
      duration: 'month',
    },
  },
  {
    amount: 8000,
    currency: 'EUR',
    interval: {
      amount: 1,
      duration: 'month',
    },
  },
];

export const PRODUCT_OPTIONS: Array<OptionOf<StellaxAiReportType>> = [
  {
    label: `Basisrapport - ${getPriceWithInterval(PRICES[0])}`,
    description: `Bevat huurprijs en WWS-punten check`,
    payload: StellaxAiReportType.B,
    key: 'productB',
  },
  {
    label: `Uitgebreid rapport - ${getPriceWithInterval(PRICES[1])}`,
    description: `Basisrapport plus leegwaarde en rendementsberekening`,
    payload: StellaxAiReportType.D,
    key: 'productD',
  },
];

export const stellax: AppConfig = {
  __typename: 'AppStatus_StellaxAi',
  appType: AppType.StellaxAi,
  type: 'with_config',
  slug: 'stellax-ai',
  name: 'Stellax.ai - Property scan',
  tags: ['Verhuur', 'Vastgoedbeheer', 'Leadgeneratie'],
  provider: 'Stellax B.V.',
  logo: {
    src: 'https://dathuis-stash.imgix.net/stellax_ai_logo.svg',
  },
  image: {
    src: 'https://dathuis-stash.imgix.net/stellax_ai_logo.svg',
  },
  price: PRICES,
  description,
  termsOfUse,
  compatibility: 'Beschikbaar voor alle gebruikers',
  help: {
    href: APP_HELP_LINK,
  },
  setupFlow: 'selectProduct',
  visibility: 'visible',
  cancellability: 'daily',
  wizard: 'custom',
  productOptions: PRODUCT_OPTIONS,
};
